import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import Pagination from "@/components/ventrix/Pagination";
import { formatMoney, formatDate, EmptyState } from "@/components/ventrix/atoms";
import { Wallet, BadgeCheck } from "lucide-react";

const LIMIT = 50;

// Friendly labels for the recorded method/provider shown in the queue.
const METHOD_LABELS = {
  manual: "Bank transfer",
  mpesa_manual: "M-Pesa (Paybill)",
  cash: "Cash",
  stripe: "Card",
  mpesa: "M-Pesa (STK)",
};
const methodLabel = (p) => METHOD_LABELS[p.provider] || METHOD_LABELS[p.method] || p.provider || p.method || "—";

export default function PaymentsQueue({ section = "admin" }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState("");   // pid currently being actioned

  const load = () => {
    setItems(null);
    api.get(`/v1/payments/pending?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  const review = async (invoiceId, pid, action) => {
    setBusy(pid);
    try {
      await api.post(`/v1/invoices/${invoiceId}/payments/${pid}/${action}`);
      toast.success(action === "verify" ? "Payment verified" : "Payment rejected");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <PortalLayout section={section}>
      <div className="vtx-eyebrow">/ {section === "ops" ? "Ops" : "Admin"} · Payments</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><Wallet size={24} /> Payments to verify</h1>
      <p className="text-sm text-[#475569] mt-1">Customer payments (bank transfer, M-Pesa, cash) awaiting confirmation. Verifying credits the invoice; rejecting frees it for resubmission.</p>

      <div className="vtx-card mt-6 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map(i => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && items.length === 0 && (
          <EmptyState icon={BadgeCheck} title="Nothing to verify" hint="Customer bank transfers, M-Pesa and cash payments appear here for review." />
        )}
        {items && items.length > 0 && (
          <table className="vtx-table w-full">
            <thead><tr><th>Invoice</th><th>Account</th><th>Method</th><th>Amount</th><th>Reference</th><th>Submitted</th><th></th></tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.pid} data-testid={`pending-row-${p.pid}`}>
                  <td className="font-mono font-semibold text-[#0E2F76]">{p.invoice_number}</td>
                  <td className="font-mono text-xs text-[#475569]">{p.account_id || "—"}</td>
                  <td className="text-xs font-semibold text-[#0E2F76]" data-testid={`pending-method-${p.pid}`}>{methodLabel(p)}</td>
                  <td className="font-semibold">{formatMoney(p.amount, p.currency)}</td>
                  <td className="font-mono text-xs">{p.reference || "—"}</td>
                  <td className="text-xs text-[#475569] whitespace-nowrap">{formatDate(p.created_at)}</td>
                  <td>
                    <div className="flex gap-3">
                      <button className="text-xs text-[#166534] font-bold hover:underline disabled:opacity-40" disabled={busy === p.pid}
                        onClick={() => review(p.invoice_id, p.pid, "verify")} data-testid={`queue-verify-${p.pid}`}>Verify</button>
                      <button className="text-xs text-[#d41f26] font-bold hover:underline disabled:opacity-40" disabled={busy === p.pid}
                        onClick={() => review(p.invoice_id, p.pid, "reject")} data-testid={`queue-reject-${p.pid}`}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>
    </PortalLayout>
  );
}
